<template>
  <div v-if="!isLoading" class="prepayment">
    <template v-if="step === 'input'">
      <div class="prepayment__loan">
          <template v-if="sources.length > 0">
          <TextField
            v-model="paymentAmount"
            :label="$t`Оплачиваю сейчас`"
            :masks="paymentAmountMasks"
            :rules="[
              (val) =>
                parseStringNumber(val) <= this.fullDebt ||
                maxAmountErrorText,
              (val) => (!!val && val !== '0') || 'empty',
            ]"
            :persistent="isPersistentInput"
            class="prepayment__input"
            type="tel"
          />
          <div class="chips">
            <div class="chips__detail"
              v-for="x in chips" :key="x" 
              @click="selectedAmount(x)">
                {{$currencyFormatter(x)}}
            </div></div>
          <div class="loan-info">
          <ProductButton
            class="loan-info__main"
            :title="$t`Задолженность Kaspi Red`"
            :tailText="paidAmount.toString()"
            :tailTextPaid="fullDebt"
            :isCalculator="true"
            @click="showLoanDetails"
          />
          <div
            v-if="this.fullDebt!=0"
            class="loan-info__icon"
            :class="{
              'loan-info__icon--active': isLoanDetailsVisible && loanDetails,
            }"
          >
            <div v-if="isArrowVisible" class="i-arrow"></div>
          </div>
        </div>
        <ExpandTransition :duration="200">
          <div v-if="isArrowVisible && isLoanDetailsVisible && loanDetails" class="loan-details">
            <div
              v-for="loan in loanDetails"
              :key="loan.title"
              class="loan-details__block"
            >
              <template v-if="loan.amount > 0">
                <div class="loan-details__text">
                  {{ loan.text }}
                </div>
                <div class="loan-details__amount">
                  {{ $currencyFormatter(loan.amount) }}
                </div>
              </template>
            </div>
          </div>
        </ExpandTransition>
          <div v-if="showCalculator" class="loan-details__payments_newTitle">
              {{ $t`Календарь будущих счетов` }}
          </div>
          <PaymentsTable
            class="loan-details__payments"
            :invoiceItems="invoiceItems"
            :payingAmount="currentAmount"
            compressed
          />
        </template>
      </div>
    </template>
    <template v-else>
      <ProductButton
        class="prepayment__confirmation-sum"
        :title="$t`К оплате`"
        :tailText="$currencyFormatter(currentAmount)"
        @click="showLoanDetails"
      />
    </template>
    <template v-if="sources.length > 0">
      <ProductButton
        v-if=" step === 'confirmation'"
        icon="gold"
        title="Kaspi Gold"
        :tailText="$currencyFormatter(sources[0].amount,'KZT', 2)"
      />
      <div class="prepayment__newComission">
        <span>
          {{ $t`Комиссия` + ` ${$currencyFormatter(0)}` }}
        </span>
      </div>
      <CommonButton spaced @click="onBtnClick">
        {{ buttonText }}
      </CommonButton>
    </template>
    <PaymentTerminal v-else />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { fetchRepaymentAccounts } from "@/api/accounts";
import { redPrepayment } from "@/api/payments";
import duplicatePreventor from "@/api/decorators/duplicatePreventor";
import TextField from "@/components/shared/input/TextField.vue";
import ExpandTransition from "@/components/shared/transitions/ExpandTransition.vue";
import ProductButton from "@/components/shared/button/ProductButton.vue";
import CommonButton from "@/components/shared/button/CommonButton.vue";
import PaymentTerminal from "@/components/payments/PaymentTerminal.vue";
import { fetchCalculator, fetchPrepaymentCounter } from "@/api/bills";
import PaymentsTable from "@/components/payments/PaymentsTable.vue";

const getDepositTransferLink = (accNum) =>
  `${process.env.VUE_APP_ENV_URL}/transfers/categories/own-accounts?destId=A_${accNum}&startedFrom=mybank_gold_refill`;

export default {
  components: {
    TextField,
    CommonButton,
    ExpandTransition,
    ProductButton,
    PaymentTerminal,
    PaymentsTable
  },
  data() {
    return {
      isLoading: true,
      step: "input",
      paymentAmount: "",
      sources: [],
      isLoanDetailsVisible: false,
      isArrowVisible: true,
      isPersistentInput: false,
      paymentAmountMasks: [
        (val) => val.replace(/(.{7}).+/g, "$1"),
        (val) =>
          new Intl.NumberFormat("ru-RU").format(Number(val.replace(/\D/g, ""))),
      ],
      invoiceItems: null,
      fullDebt: null,
      fineSum: null,
      clubFeeSum: null,
      renewalFeeSum: null,
      purchaseSum: null,
      showCalculator: true
    };
  },
  async mounted() {
    this.$logAnalyticEvent("mybank_red_prepayment", {
      page_path: "/bank/red/redprepayment",
      step: "amount",
      came_from:
        this.$route.query.src === "paymentcalendar" ? "calendar" : "home",
    });
    try {
      this.fetchBundle();
      await fetchPrepaymentCounter();
      const sources = await fetchRepaymentAccounts();
      this.isLoading = false;
      const calculatorData = await fetchCalculator();
      this.invoiceItems = calculatorData.invoice_items ?? null;
      if(this.invoiceItems == null || calculatorData.invoice_items.length == 0) {
        this.showCalculator= false;
      }
      this.fullDebt = calculatorData.full_debt == 0 ? this.redAccounts[0].loan : calculatorData.full_debt;
      this.fineSum = calculatorData.fine_sum ==0 ? this.redAccounts[0].unpaid_fines : calculatorData.fine_sum;
      this.purchaseSum = calculatorData.purchase_sum == 0 ? this.redAccounts[0].unpaid_purchases : calculatorData.purchase_sum;
      this.clubFeeSum = calculatorData.club_fee_sum == 0 ? this.redAccounts[0].unpaid_club_fee : calculatorData.club_fee_sum;
      this.renewalFeeSum = calculatorData.renewal_fee_sum == 0 ? this.redAccounts[0].unpaid_emb_fee : calculatorData.renewal_fee_sum
  
      this.sources = sources;
      this.isArrowVisible = this.fineSum == 0 && this.clubFeeSum == 0 && this.renewalFeeSum == 0 ? false : true
      if (this.$route.query.src == "paymentcalendar"){
        this.paymentAmount = this.$route.query.prepaymentAmount;
      }
       
    } catch {
      history.back();
    }

  },
  computed: {
    ...mapGetters("bundle", {
      redAccounts: "getRedAccounts",
      deposits: "getDeposits",
      goldAccounts: "getGoldAccounts",
    }),
    parsedAmount() {
      return this.parseStringNumber(this.paymentAmount);
    },
    paidAmount(){
      if (this.parsedAmount == 0){
        return this.fullDebt;
      } else {  
        return this.fullDebt - this.parsedAmount;
      }
    },
    maxAmountErrorText() {
      return this.$l10nformat(
        this.$t`Максимальная сумма досрочного погашения #AMOUNT#`,
        {
          AMOUNT: this.$currencyFormatter(this.fullDebt),
        }
      );
    },
    buttonText() {
      if (this.step === "input" && this.parsedAmount > 0) {
        return (
          this.$t`Погасить досрочно` + ` ${this.$currencyFormatter(this.currentAmount)}`
        );
      }

      if (this.step === "confirmation") {
        return this.$t`Подтвердить и погасить` + ` ${this.$currencyFormatter(this.currentAmount)}`;
      }

      return this.$t`Погасить досрочно`;
    },
    chips(){
      let arr = [2000, 5000, 10000];
      let filteredArr = arr.filter(item => item != this.fullDebt && item < this.fullDebt);
      filteredArr.push(this.fullDebt);
      return filteredArr;
    },
    loanDetails() {
      return {
        unpaid: {
          text: this.$t`За покупки`,
          amount: this.purchaseSum,
        },
        clubfee: {
          text: this.$t`Клубный взнос`,
          amount: this.clubFeeSum,
        },
        embfee: {
          text: this.$t`Комиссия за перевыпуск`,
          amount: this.renewalFeeSum,
        },
        fine: {
          text: this.$t`Штраф`,
          amount: this.fineSum,
        },
      };
    },
    currentAmount() {
      return Math.min(this.parsedAmount, this.fullDebt);
    },
  },
  methods: {
    ...mapActions("bundle", ["fetchBundle"]),
    ...mapActions("system", ["showAlert"]),
    makePayment: duplicatePreventor(redPrepayment),
    parseStringNumber(amount) {
      return parseInt(amount.replace(/\D/g, ""), 10) || 0;
    },
    selectedAmount(amount){
      this.paymentAmount = amount.toString();
      this.$logAnalyticEvent("mybank_red_prepayment_chips_clicked", {
          amount: amount,
        });
    },
    showLoanDetails() {
      this.isLoanDetailsVisible = !this.isLoanDetailsVisible;
    },
    showInsufficientFundsAlert() {
      const alertPayload = {
        title: this.$t`Недостаточно средств на вашем Kaspi Gold`,
      };
      const analyticsPayload = {
        page_path: "",
        deposit: "no",
        started_from: "",
      };

      if (this.deposits.length > 0) {
        analyticsPayload.deposit = "yes";

        const goldSrc = this.goldAccounts.find(
          (gold) => gold.simple_id === this.sources[0].id
        );

        alertPayload.btnCloseText = this.$t`Назад`;
        alertPayload.btnLinkText = this.$t`Пополнить Kaspi Gold с депозита`;
        alertPayload.btnLink = getDepositTransferLink(
          goldSrc.repayment_account_number
        );
      }

      this.$logAnalyticEvent(
        "mybank_red_prepayment_warn_not_enough",
        analyticsPayload
      );

      this.$store.dispatch("system/showAlert", alertPayload);
    },
    isDuplicatedPaymentAlert() {
      const alertPayload = {
        title: this.$t`Вы только что совершили похожую оплату`,
        text: this.$t`Повторите оплату через 1 минуту.`,
      };
      this.$store.dispatch("system/showAlert", alertPayload);
    },
    async onBtnClick() {
      if (this.currentAmount > this.sources[0].amount) {
        this.showInsufficientFundsAlert();
        return;
      }

      if (this.step === "input") {
        if (this.currentAmount === 0) {
          this.isPersistentInput = true;
        } else {
          this.step = "confirmation";
        }
        return;
      }

      if (this.step === "confirmation") {
        this.$logAnalyticEvent("mybank_red_prepayment", {
          page_path: "/bank/red/redprepayment",
          step: "confirmation",
        });
        const duplicatedResponse = "isDuplicatedPayment";
        var res = await this.makePayment(this.currentAmount, true);
        if (res === duplicatedResponse) {
          this.isDuplicatedPaymentAlert();
          return;
        }
        this.$router.replace({
          path: "/bank/red/redPrepayment/success",
          query: {
            ...this.$route.query,
            amount: this.currentAmount,
            type:
              this.currentAmount >= this.redAccounts[0].loan
                ? "full"
                : "partial",
          },
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/colors.scss";

.prepayment {
  font-family: Roboto-Regular;
  color: $colorTextMain;

  &__input {
    //margin: 1px 0 25px;
    color: $colorGreyish;
    //margin: 1px 0 5px;
    margin-top: 10px
  }

  &__comission {
    padding: 20px 0 5px;
    font-size: 14px;
    color: $colorGreyish;
  }
  &__newComission {
    padding: 49px 0 5px;
    font-size: 14px;
    color: $colorGreyish;
  }

  &__confirmation-sum {
    margin-bottom: 1px;
  }

  .loan-info {
    display: flex;
    align-items: center;
    background-color: white;

    &__icon {
      min-width: 30px;
      padding-right: 14px;
      transform-origin: 7px;
      transition: all 0.2s ease-out;

      &--active {
        transform: rotate(90deg);
      }
    }
  }

  .loan-details {
    margin-top: 1px;
    overflow: hidden;
    padding: 0 45px 0 30px;
    background-color: #fff;

    &__block {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: 1px;
      font-size: 12px;
      line-height: 40px;
    }
    &__payments {
      margin-bottom: 20px;

    &_title {
        padding: 8px 16px;
        font-size: 12px;
        line-height: 14px;
        text-align: left;
        text-transform: uppercase;
        color: #a8a8a8;
        
      }
    &_newTitle {
        padding: 16px 0 8px 16px;
        font-size: 12px;
        line-height: 14px;
        text-align: left;
        text-transform: uppercase;
        color: #a8a8a8;
      }
    }
  }
  .chips {
    display:flex;
    flex-direction: row;
    align-items: center;
    padding: 0 14px 18px 14px;
    &__detail{
      border: 1px solid #dbdbdb;
      border-radius: 13px;
      padding: 2px 7px;
      font-size: 14px;
      margin-right: 9px;
    }
  }
}
</style>
